import { GenericServiceBase } from "../generic-service";

/**
 * Enums de domínio não são compartilhados entre os repos (só `perms.config` é): o
 * servidor os declara no `schema.prisma` e o front os redeclara à mão. Ao mexer em
 * `MetaWebhookCanal` / `MetaOnboardingFluxo` / `MetaOnboardingStatus` lá, mexa aqui.
 */
export type MetaWebhookCanal = 'WHATSAPP' | 'INSTAGRAM_META' | 'INSTAGRAM_LOGIN' | 'MESSENGER_META';

export type MetaOnboardingFluxo =
    | 'WHATSAPP_EMBEDDED_SIGNUP'
    | 'WHATSAPP_OAUTH_ADMIN'
    | 'WHATSAPP_OAUTH_USUARIO'
    | 'WHATSAPP_COEXISTENCE'
    | 'INSTAGRAM_META'
    | 'INSTAGRAM_LOGIN'
    | 'MESSENGER_META'
    | 'DESVINCULO'
    /** Chamada do console da Graph (F-53): uma etapa por execução. */
    | 'GRAPH_CONSOLE';

export type MetaOnboardingStatus = 'INICIADA' | 'SUCESSO' | 'FALHA';

/** Uma entrega de webhook, como o servidor a devolve. */
export interface IMetaWebhookLog {
    id: string;
    canal: MetaWebhookCanal;
    rota: string;
    objeto: string | null;
    campo: string | null;
    ehMensagem: boolean;
    entryId: string | null;
    wabaId: string | null;
    phoneNumberId: string | null;
    displayPhoneNumber: string | null;
    pageId: string | null;
    igId: string | null;
    metaTimestamp: string | null;
    idInbox: string | null;
    idConta: string | null;
    payloadJson: unknown;
    truncado: boolean;
    tamanhoBytes: number;
    assinaturaValida: boolean | null;
    preservado: boolean;
    recebidoEm: string;
    expiraEm: string;
}

/** Uma etapa de onboarding. */
export interface IMetaOnboardingLog {
    id: string;
    idExecucao: string;
    fluxo: MetaOnboardingFluxo;
    etapa: string;
    status: MetaOnboardingStatus;
    ordem: number;
    idInbox: string | null;
    idConta: string | null;
    idUsuario: string | null;
    idAdministrador: string | null;
    wabaId: string | null;
    phoneNumberId: string | null;
    businessId: string | null;
    pageId: string | null;
    igId: string | null;
    numero: string | null;
    metodo: string | null;
    url: string | null;
    httpStatus: number | null;
    requisicaoJson: unknown;
    respostaJson: unknown;
    erroMensagem: string | null;
    erroCode: number | null;
    erroSubcode: number | null;
    fbtraceId: string | null;
    duracaoMs: number | null;
    preservado: boolean;
    criadoEm: string;
    expiraEm: string;
}

/** Página + o TTL vigente, que a tela mostra para explicar por que linhas somem. */
export interface IPaginaTrilha<T> {
    total: number;
    itens: T[];
    ttlDias: number;
}

/** Contagem por canal e `field`. */
export interface IResumoWebhooks {
    canal: MetaWebhookCanal;
    campo: string | null;
    _count: { _all: number };
}

/** Espelho do `ConsultarMetaWebhooksDto`. */
export class MetaWebhookFiltro {
    pagina = 1;
    limite = 50;
    canal?: MetaWebhookCanal;
    campo?: string;
    objeto?: string;
    idInbox?: string;
    idConta?: string;
    wabaId?: string;
    phoneNumberId?: string;
    pageId?: string;
    igId?: string;
    entryId?: string;
    ehMensagem?: boolean;
    preservado?: boolean;
    truncado?: boolean;
    busca?: string;
    desde?: string;
    ate?: string;
}

/** Espelho do `ConsultarMetaOnboardingDto`. */
export class MetaOnboardingFiltro {
    pagina = 1;
    limite = 50;
    fluxo?: MetaOnboardingFluxo;
    status?: MetaOnboardingStatus;
    etapa?: string;
    idExecucao?: string;
    idInbox?: string;
    idConta?: string;
    idUsuario?: string;
    wabaId?: string;
    phoneNumberId?: string;
    businessId?: string;
    pageId?: string;
    igId?: string;
    numero?: string;
    /** String para casar com o seletor; o DTO do servidor converte para número. */
    erroCode?: string;
    preservado?: boolean;
    busca?: string;
    desde?: string;
    ate?: string;
}

/** Dimensão pedida ao catálogo — espelha o `DimensaoDoCatalogo` do servidor. */
export type DimensaoDoCatalogo =
    | 'waba'
    | 'pid'
    | 'page'
    | 'ig'
    | 'inbox'
    | 'conta'
    | 'usuario'
    | 'campo'
    | 'error-code';

/**
 * De onde o valor veio.
 *
 * `TRILHA` sozinho é o item mais informativo da lista: o id chegou nos webhooks
 * e NÃO pertence a nenhuma inbox nossa.
 */
export type OrigemDoItem = 'DOMINIO' | 'TRILHA' | 'AMBOS';

/** Uma opção do seletor. */
export interface IItemDeCatalogo {
    valor: string;
    rotulo: string;
    descricao?: string | null;
    idInbox?: string | null;
    nomeInbox?: string | null;
    plataforma?: string | null;
    idConta?: string | null;
    nomeConta?: string | null;
    origem: OrigemDoItem;
    ocorrencias?: number | null;
    grupo?: string | null;
}

/** Um preset do console da Graph. */
export interface IPresetDaGraph {
    id: string;
    grupo: 'TOKEN' | 'WABA' | 'PAGAMENTO' | 'TEMPLATE';
    rotulo: string;
    explicacao: string;
    caminho: string;
    query?: Record<string, string>;
    requer: ('waba' | 'pid' | 'business' | 'page')[];
}

export interface IPresetsResposta {
    grupos: Record<string, string>;
    presets: IPresetDaGraph[];
}

/** Corpo de `POST /admin/meta-trilha/graph`. */
export interface IRequisicaoGraph {
    idInbox?: string;
    fonteDoToken?: 'AUTO' | 'WHATSAPP_META' | 'INSTAGRAM_META_PAGINA' | 'INSTAGRAM_LOGIN' | 'AMBIENTE';
    caminho: string;
    metodo?: string;
    versao?: string;
    query?: Record<string, string | number | boolean>;
    corpo?: Record<string, unknown>;
    confirmarEscrita?: boolean;
    timeoutMs?: number;
}

/** Resposta do console da Graph. */
export interface IResultadoGraph {
    ok: boolean;
    fonteDoToken: string;
    tokenSufixo: string;
    metodo: string;
    url: string;
    httpStatus: number | null;
    duracaoMs: number;
    corpo: unknown;
    erro?: { code: number | null; subcode: number | null; fbtraceId: string | null; mensagem: string | null } | null;
}

/**
 * Remove chaves vazias antes de mandar: `FetchUtil` serializa `undefined` e
 * string vazia como parâmetro presente, e o `ValidationPipe` do servidor recusa
 * (`desde` vazio não é ISO-8601, e o 400 vira "erro ao carregar" na tela).
 */
function limpar(filtro: object): Record<string, unknown> {
    const saida: Record<string, unknown> = {};
    for (const [chave, valor] of Object.entries(filtro)) {
        if (valor === undefined || valor === null || valor === '') continue;
        if (Array.isArray(valor) && !valor.length) continue;
        saida[chave] = valor;
    }
    return saida;
}

/**
 * Trilha Meta — só admin.
 *
 * Um serviço só para as três telas (webhooks, onboarding e console): elas
 * compartilham o mesmo prefixo de rota e o mesmo `limpar`, e separar em três
 * classes só espalharia a duplicação.
 */
export class MetaTrilhaService extends GenericServiceBase {
    constructor() { super('admin/meta-trilha') }

    // ── Webhooks ────────────────────────────────────────────────────────────

    async listarWebhooks(filtro: MetaWebhookFiltro): Promise<IPaginaTrilha<IMetaWebhookLog>> {
        return await this.f.get('/webhooks', limpar(filtro)) as IPaginaTrilha<IMetaWebhookLog>;
    }

    async resumoWebhooks(filtro: MetaWebhookFiltro): Promise<IResumoWebhooks[]> {
        return await this.f.get('/webhooks/resumo', limpar(filtro)) as IResumoWebhooks[];
    }

    async detalheWebhook(id: string): Promise<IMetaWebhookLog> {
        return await this.f.get(`/webhooks/${id}`) as IMetaWebhookLog;
    }

    async preservarWebhooks(ids: string[], preservado: boolean): Promise<{ afetadas: number }> {
        return await this.f.post('/webhooks/preservar', { ids, preservado }) as { afetadas: number };
    }

    // ── Onboarding ──────────────────────────────────────────────────────────

    async listarOnboarding(filtro: MetaOnboardingFiltro): Promise<IPaginaTrilha<IMetaOnboardingLog>> {
        return await this.f.get('/onboarding', limpar(filtro)) as IPaginaTrilha<IMetaOnboardingLog>;
    }

    async linhaDoTempo(idExecucao: string): Promise<IMetaOnboardingLog[]> {
        return await this.f.get(`/onboarding/execucao/${idExecucao}`) as IMetaOnboardingLog[];
    }

    async detalheOnboarding(id: string): Promise<IMetaOnboardingLog> {
        return await this.f.get(`/onboarding/${id}`) as IMetaOnboardingLog;
    }

    async preservarOnboarding(
        alvo: { ids?: string[]; idExecucao?: string },
        preservado: boolean,
    ): Promise<{ afetadas: number }> {
        return await this.f.post('/onboarding/preservar', { ...alvo, preservado }) as { afetadas: number };
    }

    // ── Catálogo dos seletores ──────────────────────────────────────────────

    /**
     * Opções de um filtro. A busca é feita NO SERVIDOR: uma instalação com
     * milhares de inboxes não cabe num select, e baixar tudo para filtrar aqui
     * trocaria uma query por um megabyte de JSON a cada abertura de tela.
     */
    async catalogo(dimensao: DimensaoDoCatalogo, busca?: string, limite = 50): Promise<IItemDeCatalogo[]> {
        return await this.f.get('/catalogo', limpar({ dimensao, busca, limite })) as IItemDeCatalogo[];
    }

    // ── Console da Graph ────────────────────────────────────────────────────

    async presetsDaGraph(): Promise<IPresetsResposta> {
        return await this.f.get('/graph/presets') as IPresetsResposta;
    }

    async executarGraph(req: IRequisicaoGraph): Promise<IResultadoGraph> {
        return await this.f.post('/graph', req) as IResultadoGraph;
    }
}

/** Rótulos em PT-BR. Um lugar só, usado pelas três telas. */
export const ROTULO_CANAL: Record<MetaWebhookCanal, string> = {
    WHATSAPP: 'WhatsApp Cloud',
    INSTAGRAM_META: 'Instagram (via Página)',
    INSTAGRAM_LOGIN: 'Instagram Login',
    MESSENGER_META: 'Messenger',
};

/** Rótulo curto de cada dimensão, para o seletor não repetir a tradução. */
export const ROTULO_DIMENSAO: Record<DimensaoDoCatalogo, string> = {
    waba: 'WABA',
    pid: 'Phone Number ID',
    page: 'Page ID',
    ig: 'Instagram',
    inbox: 'Inbox',
    conta: 'Conta',
    usuario: 'Usuário',
    campo: 'Field do webhook',
    'error-code': 'error.code da Meta',
};

export const ROTULO_FLUXO: Record<MetaOnboardingFluxo, string> = {
    WHATSAPP_EMBEDDED_SIGNUP: 'WhatsApp — Embedded Signup',
    WHATSAPP_OAUTH_ADMIN: 'WhatsApp — OAuth (admin)',
    WHATSAPP_OAUTH_USUARIO: 'WhatsApp — OAuth (usuário)',
    WHATSAPP_COEXISTENCE: 'WhatsApp — Coexistência',
    INSTAGRAM_META: 'Instagram (via Página)',
    INSTAGRAM_LOGIN: 'Instagram Login',
    MESSENGER_META: 'Messenger',
    DESVINCULO: 'Desvínculo',
    GRAPH_CONSOLE: 'Console da Graph',
};
